import React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import InputAdornment from '@mui/material/InputAdornment';
import { useReadLocationTipsQuery } from 'services/location';
import {
    Box,
    FormGroup,
    FormControl,
    InputLabel,
    Button,
    TextField,
    Typography,
    Stack,
    MenuItem,
    Checkbox,
    FormLabel,
    Select,
    OutlinedInput
} from '@mui/material';

import { MuiChipsInput } from 'components/ChipsInput';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { updateMonitorCreateFilter, updateMonitorFilter } from 'domain/monitor';
import { PET_COUNTS } from 'shared/constants';
import { BED_COUNTS } from 'shared/constants';
import { BEDROOM_COUNTS } from 'shared/constants';
import { Controller } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch } from 'shared/hooks';
import { useAppSelector } from 'shared/hooks';
import { selectPlatforms } from 'store/platformsSlice';
import { readPlatforms } from 'store/platformsSlice';
import { DATE_PERIODS } from 'shared/constants';
import * as z from 'zod';
import type { BedSelectItem, MonitorCreateViewResource } from 'shared/types';
import type { BedroomSelectItem } from 'shared/types';
import type { DatePeriod } from 'shared/types';
import type { Monitor } from 'shared/types';
import type { MonitorCustomPeriod } from 'shared/types';
import type { MonitorSlidingPeriod } from 'shared/types';
import type { MonitorFilterItem } from 'shared/types';
import type { PetSelectItem } from 'shared/types';
import type { Platform } from 'shared/types';
import { useReadPlatformsQuery } from 'services/platform';

export interface MonitorCreateFormViewProps {
    value: MonitorCreateViewResource,
    onChange?: Function,
    onSubmit?: Function,
    onBack?: Function,
};

type Period = {
    id: number,
    name: string
}

const FormSchema = z.object({
    title: z
        .string()
        .nonempty(),
    description: z
        .string(),
    enabled: z
        .boolean(),
    bedrooms: z
        .string()
        .nonempty(),
    beds: z
        .string()
        .nonempty(),
    locations: z
        .array(z.string())
        .nonempty()
        .refine((value) => {
            return value.every((location: string) => {
                if (location === null) {
                   return false
                }
                const numberOfSpaces = location.split(" ").length - 1

                return numberOfSpaces < 3
            })
        }, {
            message: "The location cannot contain more than 3 words.",
        }),
    collectNearByLocations: z
        .boolean()
}).passthrough();

export const MonitorCreateFormView = ({ value, onSubmit, onBack }: MonitorCreateFormViewProps): JSX.Element => {
    const navigate = useNavigate()
    const {
        data: readPlatformsQueryData,
        isSuccess: readPlatformsQueryIsSuccess
    } = useReadPlatformsQuery()

    const {
        handleSubmit,
        formState: { errors, submitCount, isSubmitting },
        control,
        getValues,
        reset,
        trigger,
        setValue
    } = useForm<MonitorCreateViewResource>({
        resolver: zodResolver(FormSchema),
        defaultValues: value
    });

    const getPeriodFromMonitor = (monitor: MonitorCreateViewResource) => {
        if (Array.isArray(monitor?.customPeriods) && monitor.customPeriods.length > 0) {
            return DATE_PERIODS[3]
        }

        if (Array.isArray(monitor?.slidingPeriods) && monitor.slidingPeriods.length > 0) {
            if (monitor.slidingPeriods[0].offset === 0) {
                return DATE_PERIODS[0]
            }

            if (monitor.slidingPeriods[0].offset === 1) {
                return DATE_PERIODS[1]
            }

            return DATE_PERIODS[0];
        }

        return DATE_PERIODS[0];
    }

    const [monitor, setMonitor] = useState<MonitorCreateViewResource>(value);
    const periodFromMonitor = getPeriodFromMonitor(monitor);
    const [period, setPeriod] = useState<Period | undefined>(periodFromMonitor);

    const submitFormHandler = (value: MonitorCreateViewResource, event: any) => {
        event.preventDefault();

        if (typeof onSubmit === "function") {
            onSubmit({
                event: event,
                value: value
            });
        }
    }

    const onBackHandler = (event: any) => {
        if (typeof onBack === "function") {
            onBack({
                event: event
            });
        }

        navigate('/market');
    }   

    const renderDaysAhead = () => {
        const renderInput = () => {
            return (<Controller
                name="daysAhead"
                control={control}
                render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { invalid, isTouched, isDirty, error},
                    formState: { dirtyFields, touchedFields },
                }) => (
                    <FormControl required sx={{ mt: 3, '& *': { fontSize: '20px' } }}>
                        <TextField
                            label='Days ahead'
                            
                            InputProps={{
                                inputProps: { min: 0 },
                            }}
                            type="number"
                            fullWidth
                            error={!!errors['daysAhead']}
                            helperText={errors['daysAhead'] ? errors['daysAhead'].message : ''}
                            value={value}
                            name={name}
                            onChange={
                                async (event) => {
                                    const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.value)
                                    updateMonitorDataOnForm(newMonitor);
                                    validateFields();
                                }
                            }
                        />
                    </FormControl>
                )}
            />)
        }

        return (
            (period?.id === 1000 && value !== undefined ? '' : renderInput())
        )
    }

    const renderPeriod = () => {     
        const customPeriod = () => (
            <>
                <Controller
                    control={control}
                    name="customPeriods"
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <>
                             <FormControl sx={{ width: 300, marginTop: '10px' }}>
                    <DatePicker
                        label="From"
                                    value={value?.[0]?.startDateTimeUtc === undefined ? DateTime.now() : DateTime.fromISO(value?.[0]?.startDateTimeUtc)}
                        onChange={
                            async (value) => {
                                const monitor = getValues();

                                if (monitor?.customPeriods?.[0].endDateTimeUtc !== undefined) {
                                    const customPeriod: MonitorCustomPeriod = {
                                        startDateTimeUtc: value === null ? '' : value.toUTC().toString(),
                                        endDateTimeUtc: DateTime.fromISO(monitor.customPeriods[0].endDateTimeUtc).toUTC().toString()
                                    }

                                    const newMonitor: MonitorCreateViewResource = await recalculateMonitor(monitor, 'customPeriods', [customPeriod])
                                    updateMonitorDataOnForm(newMonitor);
                                    validateFields();
                                }
                            }
                            }
                    />
                </FormControl>
                <FormControl sx={{ width: 300, marginTop: '10px'}}>
                    <DatePicker
                        label="To"
                        value={value?.[0]?.endDateTimeUtc === undefined ? DateTime.now() : DateTime.fromISO(value?.[0]?.endDateTimeUtc)}
                        onChange={
                            async (value) => {
                                const monitor = getValues();

                                if (monitor?.customPeriods?.[0].startDateTimeUtc !== undefined) {
                                    const customPeriod: MonitorCustomPeriod = {
                                        startDateTimeUtc: DateTime.fromISO(monitor.customPeriods[0].startDateTimeUtc).toUTC().toString(),
                                        endDateTimeUtc: value === null ? '' : value.toUTC().toString()
                                    }

                                    const newMonitor: MonitorCreateViewResource = await recalculateMonitor(monitor, 'customPeriods', [customPeriod])
                                    updateMonitorDataOnForm(newMonitor);
                                    validateFields();
                                }
                            }
                        }
                    />
                            </FormControl>
                        </>
                    )}
                />        
            </>
        )

        return (
            <>
                {period?.id === 1000 && value !== undefined ? customPeriod(): ''}
            </>
        )
    }

    const {
        data: readLocationTipsData,
        isLoading: readLocationTipsDataIsLoading
    } = useReadLocationTipsQuery(undefined);

    const recalculateMonitor = async (monitor: MonitorCreateViewResource, name: string, value: any): Promise<MonitorCreateViewResource> => {
        const monitorFilterItem: MonitorFilterItem = {
            name: name,
            value: value
        };

        const newMonitor: MonitorCreateViewResource = updateMonitorCreateFilter({ monitor: monitor, monitorFilterItem });
        return newMonitor;
    } 

    const updateMonitorDataOnForm = (monitor: MonitorCreateViewResource) => {
        reset(monitor, { keepDirtyValues: true, keepSubmitCount: true })
        setMonitor(monitor)
    }

    const validateFields = () => {
        if (submitCount > 0) {
            trigger();
        }
    }




    return (
        <>
            <Box sx={{
                    display: 'Grid',
                }
            }
                component="form" onSubmit={handleSubmit(submitFormHandler)}
            >
                <Typography sx={{fontSize: '28px'}} variant="h4">Launch Spy</Typography>
                <Controller
                    name="title"
                    control={control}
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <TextField
                            sx={{ mb: 2, mt: 2, '& *': { fontSize: '20px' } }}
                            label='Title *'
                            fullWidth
                            error={!!errors['title']}
                            helperText={errors['title'] ? errors['title'].message : ''}
                            value={value}
                            name={name}
                            onChange={onChange}
                            onBlur={
                                async (event) => {
                                    const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.value)
                                    updateMonitorDataOnForm(newMonitor);
                                    validateFields();
                                }
                            }
                        />
                    )}
                />
                <Controller
                    name="description"
                    control={control}
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <TextField
                            sx={{ mb: 2, mt: 1, '& *': { fontSize: '20px' } }}
                            label='Description'
                            fullWidth
                            error={!!errors['description']}
                            helperText={errors['description'] ? errors['description'].message : ''}
                            value={value}
                            name={name}
                            onChange={
                                async (event) => {
                                    const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.value)
                                    updateMonitorDataOnForm(newMonitor);
                                    validateFields();
                                }
                            }
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="locations"
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <FormControl required sx={{ mt: 1, '& *': { fontSize: '20px' } }}>
                                <MuiChipsInput
                                autoCompleteOptions={readLocationTipsData?.slice(0, 3) || []}
                                    helperText={errors['locations'] ? errors['locations'].message : ''}
                                    error={!!errors['locations']}
                                    clearInputOnBlur={true}
                                    value={value}
                                    name={name}
                                    label="Locations *"
                                    onChange={
                                        async (value) => {
                                            const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, value);
                                            updateMonitorDataOnForm(newMonitor);
                                            validateFields();
                                    }}
                                    />
                            </FormControl>
                    )}
                />       

                <Controller
                    control={control}
                    name="collectNearByLocations"
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <FormControl sx={{ my: 0, mt: 2,  }} component="fieldset" variant="standard">
                            <FormGroup sx={{
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                justifyContent: 'start',
                                alignItems: 'center',
                            }}>
                                <FormLabel sx={{ fontSize: '20px'} }>Collect Near By Locations</FormLabel>
                                <div>
                                    <Checkbox
                                        size="large"
                                        sx={{ paddingLeft: 0} }
                                        name={name}
                                        checked={value}
                                        onChange={
                                            async (event) => {
                                                const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.checked);
                                                updateMonitorDataOnForm(newMonitor);
                                                validateFields();
                                            }
                                        }
                                    />
                                </div>
                            </FormGroup>
                        </FormControl>
                    )}
                />

                <Controller
                    control={control}
                    name="bedrooms"
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <FormControl required sx={{ width: 350, mt: 2, '& *': { fontSize: '20px' } }}>
                            <InputLabel id="bedrooms-label">Bedrooms</InputLabel>
                            <Select
                                labelId="bedrooms-label"
                                id="bedrooms-selector"
                                name={name}
                                value={value}
                                onChange={
                                    async (event) => {
                                        const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.value);
                                        updateMonitorDataOnForm(newMonitor);
                                        validateFields();
                                    }
                                }
                                input={<OutlinedInput label="Bedrooms" />}
                            >
                                {BEDROOM_COUNTS.map((obj: BedroomSelectItem) => (
                                    <MenuItem key={obj.label} value={obj.label}>
                                        {obj.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />

                <Controller
                    control={control}
                    name="beds"
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <FormControl required sx={{ width: 350, margin: '0 0 0 0', mt: 3, '& *': { fontSize: '20px' } }}> 
                            <InputLabel id="beds-label">Beds</InputLabel>
                            <Select
                                labelId="beds-label"
                                id="beds-selector"
                                name={name}
                                value={value}
                                onChange={
                                    async (event) => {
                                        const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.value);
                                        updateMonitorDataOnForm(newMonitor);
                                        validateFields();
                                    }
                                }
                                input={<OutlinedInput label="Beds" />}
                            >
                                {BED_COUNTS.map((obj: BedSelectItem) => (
                                    <MenuItem key={obj.label} value={obj.label}>
                                        {obj.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />

                <Controller
                    control={control}
                    name="pets"
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <FormControl required sx={{ width: 350, margin: '0 0 0 0', mt: 3, '& *': { fontSize: '20px' } }}>
                            <InputLabel id="pets-label">Pets</InputLabel>
                            <Select
                                labelId="pets-label"
                                id="pets-selector"
                                name={name}
                                value={value}
                                onChange={
                                    async (event) => {
                                        const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.value);
                                        updateMonitorDataOnForm(newMonitor);
                                        validateFields();
                                    }
                                }
                                input={<OutlinedInput label="Pets" />}
                            >
                                {PET_COUNTS.map((obj: PetSelectItem) => (
                                    <MenuItem key={obj.value} value={obj.value}>
                                        {obj.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />   

                <Controller
                    control={control}
                    name="platformId"
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <FormControl sx={{ width: 350, mt: 3, '& *': { fontSize: '20px' } }}>
                            <InputLabel id="platforms-multiple-checkbox-label">Platform *</InputLabel>
                            <Select
                                labelId="platforms-multiple-checkbox-label"
                                id="platforms-multiple-checkbox"                         
                                name={name}
                                value={value}
                                onChange={                                    
                                    async (event) => {
                                        const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.value);
                                        updateMonitorDataOnForm(newMonitor);
                                        validateFields();
                                    }
                                }
                                input={<OutlinedInput label="Platforms" />}
                            >
                                {readPlatformsQueryData?.list?.map((obj: any) => (
                                    <MenuItem key={obj.id} value={obj.id}>
                                        {obj.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />  

                <Controller
                    control={control}
                    name="period"
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <FormControl required sx={{ width: 350, margin: '0 0 0 0', mt: 3, '& *': { fontSize: '20px' } }}>
                            <InputLabel id="period-checkbox-label">Period</InputLabel>
                            <Select
                                labelId="period-checkbox-label"
                                id="period-checkbox"
                                name={name}
                                value={period?.id.toString()}
                                onChange={
                                    async (event) => {
                                        const foundPeriod: DatePeriod | undefined = DATE_PERIODS.find((period) => period.id === parseInt(event.target.value))

                                        if (foundPeriod !== undefined) {
                                            setPeriod(foundPeriod);

                                            if (foundPeriod?.value !== undefined) {
                                                let newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), 'daysAhead', 1);
                                                newMonitor = await recalculateMonitor(newMonitor, 'customPeriods', []);

                                                newMonitor = await recalculateMonitor(newMonitor, 'slidingPeriods', [foundPeriod.value]);
                                                updateMonitorDataOnForm(newMonitor);
                                                validateFields();
                                                return;
                                            } 
 
                                            const customPeriod: MonitorCustomPeriod = {
                                                startDateTimeUtc: DateTime.now().plus({ days: 0 }).toUTC().toString(),
                                                endDateTimeUtc: DateTime.now().plus({ days: 1 }).toUTC().toString()
                                            }


                                            let newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), 'daysAhead', 0);
                                            newMonitor = await recalculateMonitor(newMonitor, 'customPeriods', [customPeriod]);
                                            newMonitor = await recalculateMonitor(newMonitor, 'slidingPeriods', []);
                                            updateMonitorDataOnForm(newMonitor);
                                            validateFields();


                                        }
                                    }
                                }
                                input={<OutlinedInput label="Period" />}
                            >
                                {DATE_PERIODS
                                    .filter((datePeriod) => datePeriod.enabled === true)
                                    .map((obj: any) => (
                                    <MenuItem key={obj.id} value={obj.id}>
                                        {obj.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />  
                {renderDaysAhead()}
                {renderPeriod()}

                <Controller
                    name="priceForComparison"
                    control={control}
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <FormControl required sx={{ mt: 3, '& *': { fontSize: '20px' } } }>
                            <TextField
                                label='Total price you charge (including cleaning fee)'
                                InputProps={{
                                    startAdornment: "$",
                                    inputProps: { min: 0 },
                                    endAdornment: <InputAdornment position="end">/per day</InputAdornment>
                                }}
                                type="number"
                                fullWidth
                                error={!!errors['priceForComparison']}
                                helperText={errors['priceForComparison'] ? errors['priceForComparison'].message : ''}
                                value={value}
                                name={name}
                                onChange={
                                    async (event) => {
                                        const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.value)
                                        updateMonitorDataOnForm(newMonitor);
                                        validateFields();
                                    }
                                }
                            />
                        </FormControl>
                    )}
                />

                <Controller
                    control={control}
                    name="enabled"
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState: { dirtyFields },
                    }) => (
                        <FormControl sx={{ mt: 1 }} component="fieldset" variant="standard">
                            <FormGroup sx={{
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                justifyContent: 'start',
                                alignItems: 'center',
                            }}>
                                <FormLabel sx={{ fontSize: '20px' }}>Enabled</FormLabel>
                                <div>
                                    <Checkbox
                                        sx={{ paddingLeft: 0 }}
                                        size="large"
                                        name={name}
                                        checked={value}
                                        onChange={
                                            async (event) => {
                                                const newMonitor: MonitorCreateViewResource = await recalculateMonitor(getValues(), name, event.target.checked);
                                                updateMonitorDataOnForm(newMonitor);
                                                validateFields();
                                            }
                                        }
                                    />
                                </div>
                            </FormGroup>
                        </FormControl>
                    )}
                />  

                <FormControl sx={{mt: 2}}>
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between'} }>
                        <Button
                            type="submit"
                            variant="contained"
                            color="success"
                            sx={{
                                minWidth: 170,
                                minHeight: 48,
                                fontSize: '20px',
                                textTransform: 'none',
                                borderWidth: '2px!important',
                   
                            }}
                        >
                            Create
                        </Button>
                        <Button
                            type="button"
                            variant="contained"
                            color="success"
                            onClick={onBackHandler}
                            sx={{
                                color: 'rgba(93, 162, 80, 1)',
                                minWidth: 170,
                                minHeight: 48,
                                fontSize: '20px',
                                textTransform: 'none',
                                borderWidth: '2px!important',
                                background: `#FFFF`
                            }}
                        >Back</Button>
                    </Stack>
                </FormControl>
            </Box>
        </>
    )
}